import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, MapPinIcon, PhoneIcon, RefreshCwIcon, WrenchIcon, HelpCircleIcon, UserIcon, LoaderIcon, StarIcon } from 'lucide-react';
import { ChampionLogo } from './ChampionLogo';
interface BrandedQuoteFormProps {
  initialZip?: string;
  serviceSlug?: string;
  onClose: () => void;
}
export function BrandedQuoteForm({
  initialZip = '',
  serviceSlug = '',
  onClose
}: BrandedQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    // Form identifier
    formName: 'Champion_Windows_form_v1',
    // Hidden fields
    alid: '',
    oid: '',
    source: 'branded_landing_page',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    // New qualifying fields
    projectNature: '',
    windowCount: '',
    homeOwnership: '',
    // Existing visible fields
    taskName: 'Windows',
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Zip code lookup for city and state
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        // Zip lookup failed — user can still enter manually
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone number formatting: xxx-xxx-xxxx
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone_primary: formatted
    }));
  };
  // Load TrustedForm script and capture cert URL
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    // Poll for the TrustedForm cert URL
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // Silently continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP address
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) {
          setFormData(prev => ({
            ...prev,
            ip: data.ip
          }));
        }
      }).catch(() => {});
    } catch (e) {
      // Fetch not available
    }
  }, []);
  const totalSteps = 5;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  // Validate street address: must have a house number + street name
  const validateAddress = (address: string): boolean => {
    // Pattern: starts with digits, then space, then at least 2 chars of street name
    const addressPattern = /^\d+\s+\S{2,}/;
    return addressPattern.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear address error as user types, re-validate on blur
    if (name === 'address1') {
      setAddressError('');
    }
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Capture final cert value before submit
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    // Build payload, renaming cert → TrustedFormCert
    const {
      cert,
      ...restFormData
    } = formData;
    const payload = {
      ...restFormData,
      TrustedFormCert: finalCert
    };
    // Build URL parameters from all form data
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzbmya/?${params.toString()}`;
    console.log('Submitting to Champion webhook:', webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, but continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const getNextBusinessDay = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    if (date.getDay() === 6) date.setDate(date.getDate() + 2);
    if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const ChampionHeader = ({
    hideExit = false
  }: {
    hideExit?: boolean;
  }) => <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-773">
      <ChampionLogo size="md" data-id="element-774" />
      <div className="flex items-center gap-6" data-id="element-775">
        {!hideExit && <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#CC0000] transition-colors flex items-center gap-1" data-id="element-776">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-777" />
            <span className="hidden sm:inline" data-id="element-778">Exit</span>
          </button>}
      </div>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-779">
        <ChampionHeader hideExit data-id="element-780" />

        {/* Hero Banner — Phone Call Focused */}
        <div className="relative w-full h-64 md:h-80 flex items-center" data-id="element-781">
          <div className="absolute inset-0 z-0" data-id="element-782">
            <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Beautiful home exterior" className="w-full h-full object-cover" data-id="element-783" />
          </div>
          <div className="absolute inset-0 z-10" data-id="element-784">
            <div className="absolute left-0 top-0 bottom-0 w-full md:w-2/3 bg-[#003DA5]/90 flex flex-col justify-center px-8 md:px-16" data-id="element-785">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-786">
                Your Quote Request Has
                <br data-id="element-787" />
                Been Submitted!
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-788">
                A Champion representative will be calling you shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Keep Your Phone Nearby Section */}
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center" data-id="element-789">
          <div className="flex justify-center mb-6" data-id="element-790">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-791">
              <PhoneIcon className="w-10 h-10 text-[#003DA5]" data-id="element-792" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-793">
            Keep your phone nearby!
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-794">
            A Champion representative will be reaching out to you at{' '}
            <span className="font-semibold text-gray-900" data-id="element-795">
              {formData.phone_primary}
            </span>{' '}
            to discuss your window project and schedule your free in-home
            estimate. The call typically comes within one business day.
          </p>
        </div>

        {/* Homeowner Info — Expanded with form answers */}
        <div className="max-w-4xl mx-auto w-full px-6 py-12 text-center border-b border-gray-200" data-id="element-796">
          <h2 className="text-[#003DA5] font-bold text-sm uppercase tracking-wider mb-6" data-id="element-797">
            Your Submission Details
          </h2>
          <div className="text-gray-900 text-lg mb-2" data-id="element-798">
            <p className="font-semibold text-xl" data-id="element-799">
              {formData.first_name} {formData.last_name}
            </p>
            <p data-id="element-800">{formData.address1}</p>
            <p data-id="element-801">
              {formData.city}, {formData.state} {formData.postal_code}
            </p>
            <p data-id="element-802">{formData.phone_primary}</p>
            <p className="text-gray-500" data-id="element-803">{formData.email}</p>
          </div>

          <div className="mt-6 inline-flex flex-col gap-2 text-sm text-gray-600" data-id="element-804">
            <div className="flex items-center justify-center gap-2" data-id="element-805">
              <span className="font-semibold text-gray-800" data-id="element-806">Project Type:</span>
              <span data-id="element-807">{formData.projectNature || '—'}</span>
            </div>
            <div className="flex items-center justify-center gap-2" data-id="element-808">
              <span className="font-semibold text-gray-800" data-id="element-809">Windows:</span>
              <span data-id="element-810">{formData.windowCount || '—'}</span>
            </div>
            <div className="flex items-center justify-center gap-2" data-id="element-811">
              <span className="font-semibold text-gray-800" data-id="element-812">Homeowner:</span>
              <span data-id="element-813">{formData.homeOwnership || '—'}</span>
            </div>
          </div>
        </div>

        {/* What to Expect Section */}
        <div className="max-w-6xl mx-auto w-full px-6 py-12" data-id="element-814">
          <div className="flex flex-col md:flex-row gap-8" data-id="element-815">
            <div className="w-full md:w-1/2 relative min-h-[300px] md:min-h-[400px] rounded-lg overflow-hidden" data-id="element-816">
              <img src="https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Couple reviewing documents" className="absolute inset-0 w-full h-full object-cover" data-id="element-817" />
              <div className="absolute inset-0 bg-black/40 flex items-center p-10" data-id="element-818">
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" data-id="element-819">
                  What to Expect
                  <br data-id="element-820" />
                  During Your Free In-
                  <br data-id="element-821" />
                  Home Estimate
                </h2>
              </div>
            </div>
            <div className="w-full md:w-1/2 flex flex-col gap-4" data-id="element-822">
              <div className="bg-gray-50 p-8 rounded-lg" data-id="element-823">
                <h3 className="text-[#003DA5] font-bold text-lg mb-3" data-id="element-824">
                  Explore Products
                </h3>
                <p className="text-gray-700" data-id="element-825">
                  Your Champion representative will walk you through our product
                  options, custom styles and colors, and warranties. They'll
                  also help you compare the value of our products to that of
                  other providers.
                </p>
              </div>
              <div className="bg-white border border-gray-100 shadow-lg p-8 rounded-lg -ml-0 md:-ml-12 relative z-10" data-id="element-826">
                <h3 className="text-[#003DA5] font-bold text-lg mb-3" data-id="element-827">
                  Review Price & Financing
                </h3>
                <p className="text-gray-700" data-id="element-828">
                  Together, we'll review current promotions, calculate your
                  estimated monthly payment and energy bill savings, and
                  determine the best financing options for your project.
                </p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg" data-id="element-829">
                <h3 className="text-[#003DA5] font-bold text-lg mb-3" data-id="element-830">
                  Get a Same Day Quote
                </h3>
                <p className="text-gray-700" data-id="element-831">
                  You'll get an exact, no-obligation quote for both your new
                  products and installation, and we'll discuss next steps in the
                  home transformation process.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <section className="py-20 bg-[#F5F5F5]" data-id="element-832">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-833">
            <div className="text-center mb-12" data-id="element-834">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-835">
                What Our Customers Say
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-836">
              {[{
              name: 'Sarah M.',
              location: 'Dallas, TX',
              text: 'Champion replaced all our windows and the difference is incredible. The team was professional, on time, and left everything spotless. Our energy bills have already dropped!'
            }, {
              name: 'Robert & Linda K.',
              location: 'Columbus, OH',
              text: 'From the initial consultation to the final installation, Champion exceeded our expectations. The windows look beautiful and the financing made it so affordable.'
            }, {
              name: 'James T.',
              location: 'Denver, CO',
              text: 'I was impressed by how knowledgeable the Champion rep was. They helped us pick the perfect style and the installation was done in just one day. Highly recommend!'
            }].map((review, idx) => <div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" data-id="element-837">
                  <div className="flex text-yellow-400 mb-4" data-id="element-838">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-839" />)}
                  </div>
                  <p className="text-gray-600 mb-6 italic" data-id="element-840">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div data-id="element-841">
                    <p className="font-bold text-gray-900" data-id="element-842">{review.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-843">{review.location}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Champion Footer */}
        <footer className="bg-[#003DA5] py-16" data-id="element-844">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-845">
            <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-846">
              <div className="flex flex-col gap-8" data-id="element-847">
                <ChampionLogo size="lg" variant="light" data-id="element-848" />
                <p className="text-xs text-white/80 font-medium" data-id="element-849">
                  © {new Date().getFullYear()} findservicequotes.io. All Rights
                  Reserved.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-850">
                <div className="flex flex-col gap-4" data-id="element-851">
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-852">
                    Home
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-853">
                    Privacy Policy
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-854">
                    Terms Of Use
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-855">
                    Cookie Policy
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-856">
                    Vulnerability Policy
                  </a>
                </div>
                <div className="flex flex-col gap-4" data-id="element-857">
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-858">
                    Accessibility Policy
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-859">
                    Do Not Sell or Share My Personal Information
                  </a>
                  <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-860">
                    Limit the Use of Sensitive Personal Information
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-white/20 pt-8 flex flex-col gap-6" data-id="element-861">
              <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-862">
                *Buy 2 Windows, Get 2 Free applies to Champion's standard window
                pricing. Minimum purchase of 4 windows required. Discount
                applies to every other window purchased. **Subject to credit
                approval. Interest is billed during the promotional period, but
                all interest is waived if the purchase amount is paid in full
                within 12 months. Valid on new orders only. Cannot be combined
                with other offers. See representative for details.
              </p>
              <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-863">
                **Plan 2521. Subject to credit approval. Rates range from 15.85%
                - 21.19% APR (interest rates range from 17.99% - 24.99%). Only
                well-qualified applicants will receive an APR of 15.85%; some
                applicants may not qualify. Loan amount and rate will vary based
                on your income and creditworthiness. 12 month promotional period
                during which interest is billed but will be waived if the amount
                financed is paid in full before Promo Period expires. Monthly
                payments are not required during the Promo Period. Any unpaid
                balance and amounts owed after Promo Period will be paid over 84
                monthly payments.
              </p>
            </div>
          </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-id="element-864">
      <ChampionHeader data-id="element-865" />

      {/* TrustedForm hidden input */}
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-866" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-867">
        <motion.div className="bg-[#CC0000] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-868" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-869">
        <div className="w-full max-w-2xl" data-id="element-870">
          <div className="mb-6 h-8" data-id="element-871">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-[#CC0000] font-medium flex items-center gap-2 transition-colors" data-id="element-872">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-873" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-874">
            <AnimatePresence mode="wait" custom={direction} data-id="element-875">
              {/* STEP 1: Nature of Project */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-876">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-877">
                    What is the nature of your windows project?
                  </h1>

                  <div className="flex flex-col gap-4" data-id="element-878">
                    {[{
                  value: 'Replace',
                  desc: 'I need to replace existing windows',
                  icon: <RefreshCwIcon className="w-6 h-6" data-id="element-879" />
                }, {
                  value: 'Repair',
                  desc: 'I need to repair existing windows',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-880" />
                }, {
                  value: 'Not Sure',
                  desc: "I'm not sure yet — I'd like an expert opinion",
                  icon: <HelpCircleIcon className="w-6 h-6" data-id="element-881" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('projectNature', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.projectNature === option.value ? 'border-[#CC0000] bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'}`} data-id="element-882">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.projectNature === option.value ? 'bg-red-100 text-[#CC0000]' : 'bg-gray-50 text-[#003DA5]'}`} data-id="element-883">
                          {option.icon}
                        </div>
                        <div data-id="element-884">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-885">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-886">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: How Many Windows */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-887">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-888">
                    How many windows are involved?
                  </h1>

                  <div className="grid grid-cols-2 gap-4" data-id="element-889">
                    {['6+', '3-5', '2', '1'].map((count, idx) => <button key={idx} onClick={() => handleOptionSelect('windowCount', count)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData.windowCount === count ? 'border-[#CC0000] bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'}`} data-id="element-890">
                        <span className="font-bold text-gray-900 text-2xl" data-id="element-891">
                          {count}
                        </span>
                        <span className="text-gray-500 text-sm" data-id="element-892">
                          {count === '1' ? 'window' : 'windows'}
                        </span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Homeownership */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-893">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-894">
                    Do you own your home?
                  </h1>

                  <div className="flex flex-col gap-4" data-id="element-895">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-[#CC0000] bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'}`} data-id="element-896">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-red-100 text-[#CC0000]' : 'bg-gray-50 text-[#003DA5]'}`} data-id="element-897">
                          <UserIcon className="w-6 h-6" data-id="element-898" />
                        </div>
                        <div data-id="element-899">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-900">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-901">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: Address Information */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-902">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-903">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-904">
                    <div data-id="element-905">
                      <label htmlFor="address1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-906">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-907" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-908">
                          {addressError}
                        </p>}

                      {/* Auto-detected location from zip — clickable to edit */}
                      <div className="mt-3 flex items-center gap-2" data-id="element-909">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-910">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-911" />
                            <span data-id="element-912">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-913">
                            <MapPinIcon className="w-4 h-4 text-[#003DA5] flex-shrink-0" data-id="element-914" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-blue-300 rounded-lg text-sm font-semibold text-[#003DA5] focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" data-id="element-915" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 w-full hover:bg-blue-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-916">
                            <MapPinIcon className="w-4 h-4 text-[#003DA5] flex-shrink-0" data-id="element-917" />
                            <span className="text-sm font-semibold text-[#003DA5]" data-id="element-918">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-blue-400 ml-auto" data-id="element-919">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-920">
                            <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-921" />
                            <span className="text-sm text-gray-500" data-id="element-922">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-[#CC0000] hover:bg-red-800 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-923">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 5: Contact Information */}
              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-924">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-2 text-center" data-id="element-925">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-926">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-927">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-928">
                      <div data-id="element-929">
                        <label htmlFor="first_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-930">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="First Name" data-id="element-931" />
                      </div>
                      <div data-id="element-932">
                        <label htmlFor="last_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-933">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="Last Name" data-id="element-934" />
                      </div>
                    </div>

                    <div data-id="element-935">
                      <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-936">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="email@example.com" data-id="element-937" />
                    </div>

                    <div data-id="element-938">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-939">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-940" />
                    </div>

                    <div className="pt-4" data-id="element-941">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-942">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#CC0000] focus:ring-[#CC0000] cursor-pointer flex-shrink-0" required data-id="element-943" />
                        <span className="text-[11px] text-gray-500 leading-tight" data-id="element-944">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with Champion Windows &amp;
                          Birddog Media, including their contractors and
                          affiliates, and up to four home service companies. I
                          authorize them to email, call, or text me for
                          marketing purposes using autodialer technology, AI
                          generated or AI assisted voice, or prerecorded voice
                          messages on the phone number I provided, even if
                          listed on a DoNotCall list. Consent is not a condition
                          to purchase. I agree to the{' '}
                          <a href="#" className="underline text-[#003DA5]" data-id="element-945">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="#" className="underline text-[#003DA5]" data-id="element-946">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-[#CC0000] hover:bg-red-800 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-red-500/30" data-id="element-947">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}